import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, UserCog, Eye, EyeOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const Users = () => {
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role_id: '',
    company_id: '',
    branch_id: '',
    position: '',
    ci: '',
    phone: '',
    photo: null,
    status: 'active'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes, companiesRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/roles`),
        axios.get(`${API}/companies`)
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setCompanies(companiesRes.data);
      
      // Fetch branches for user's company
      if (user?.company_id) {
        const branchesRes = await axios.get(`${API}/branches?company_id=${user.company_id}`);
        setBranches(branchesRes.data);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = async (companyId) => {
    setFormData({ ...formData, company_id: companyId, branch_id: '' });
    if (companyId) {
      try {
        const branchesRes = await axios.get(`${API}/branches?company_id=${companyId}`);
        setBranches(branchesRes.data);
      } catch (error) {
        setBranches([]);
      }
    } else {
      setBranches([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { ...formData };
        delete updateData.password;
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success('Usuario actualizado');
      } else {
        console.log(JSON.stringify(formData, null, 2));
        await axios.post(`${API}/users`, formData);

        toast.success('Usuario creado');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este usuario permanentemente?')) return;
    try {
      await axios.delete(`${API}/users/${id}`);
      toast.success('Usuario eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleToggleStatus = async (userItem) => {
    const newStatus = userItem.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(`${API}/users/${userItem.id}`, { status: newStatus });
      toast.success(`Usuario ${newStatus === 'active' ? 'activado' : 'desactivado'}`);
      fetchData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role_id: '',
      company_id: user?.company_id || '',
      branch_id: '',
      position: '',
      ci: '',
      phone: '',
      status: 'active'
    });
    setEditingUser(null);
  };

  const openEditDialog = (userItem) => {
    setEditingUser(userItem);
    setFormData({
      email: userItem.email,
      password: '',
      name: userItem.name,
      role_id: userItem.role_id || '',
      company_id: userItem.company_id || '',
      branch_id: userItem.branch_id || '',
      position: userItem.position || '',
      ci: userItem.ci || '',
      phone: userItem.phone || '',
      status: userItem.status || 'active'
    });
    setDialogOpen(true);
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.position?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadgeColor = (roleName) => {
    if (!roleName) return 'bg-slate-100 text-slate-700';
    const name = roleName.toLowerCase();
    if (name.includes('admin')) return 'bg-red-100 text-red-700';
    if (name.includes('supervisor')) return 'bg-blue-100 text-blue-700';
    if (name.includes('manten')) return 'bg-orange-100 text-orange-700';
    if (name.includes('gerencia')) return 'bg-purple-100 text-purple-700';
    return 'bg-green-100 text-green-700';
  };

  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Gestión de Usuarios
          </h1>
          <p className="text-slate-600 mt-2">Administrar usuarios del sistema</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="btn-primary">
              <Plus size={20} className="mr-2" />
              Nuevo Usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nombre Completo *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-field"
                    placeholder="Ej: Juan Perez"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Cargo</label>
                  <input
                    type="text"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="input-field"
                    placeholder="Ej: Operador de Excavadora"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">CI / Documento</label>
                  <input
                    type="text"
                    value={formData.ci}
                    onChange={(e) => setFormData({ ...formData, ci: e.target.value })}
                    className="input-field"
                    placeholder="1234567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Teléfono</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-field"
                    placeholder="+591 70000000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Foto de perfil
                  </label>

                  <input
                    type="file"
                    accept="image/*"
                    className="input-field"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;

                      const reader = new FileReader();

                      reader.onloadend = () => {
                        setFormData({
                          ...formData,
                          photo: reader.result,
                        });
                      };

                      reader.readAsDataURL(file);
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Correo Electrónico *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                {!editingUser && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Contraseña *</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="input-field pr-10"
                        required={!editingUser}
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Rol *</label>
                  <select
                    value={formData.role_id}
                    onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="">Seleccionar rol...</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Empresa *</label>
                  <select
                    value={formData.company_id}
                    onChange={(e) => handleCompanyChange(e.target.value)}
                    className="input-field"
                    required
                  >
                    <option value="">Seleccionar empresa...</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Sucursal</label>
                  <select
                    value={formData.branch_id}
                    onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
                    className="input-field"
                  >
                    <option value="">Sin sucursal específica</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>{branch.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Estado</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="input-field"
                  >
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={() => { setDialogOpen(false); resetForm(); }}
                  className="btn-secondary"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="btn-primary">
                  {editingUser ? 'Actualizar' : 'Crear Usuario'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            placeholder="Buscar usuarios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Usuario</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Cargo</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Rol</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Empresa</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Estado</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((userItem) => (
                <tr key={userItem.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mr-3">
                        {userItem.photo ? (
                          <img src={userItem.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <span className="text-orange-600 font-semibold">
                            {userItem.name?.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{userItem.name}</p>
                        <p className="text-sm text-slate-500">{userItem.email}</p>
                        {userItem.ci && <p className="text-xs text-slate-400">CI: {userItem.ci}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{userItem.position || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`status-badge ${getRoleBadgeColor(userItem.role_name)}`}>
                      {userItem.role_name || userItem.role || 'Sin rol'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{userItem.company_name || '-'}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggleStatus(userItem)}
                      className={`status-badge cursor-pointer ${
                        userItem.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {userItem.status === 'active' ? 'Activo' : 'Inactivo'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center space-x-2">
                      <button
                        onClick={() => openEditDialog(userItem)}
                        className="p-2 hover:bg-slate-100 rounded transition-colors"
                        title="Editar"
                      >
                        <Edit size={18} className="text-orange-500" />
                      </button>
                      {userItem.id !== user?.id && (
                        <button
                          onClick={() => handleDelete(userItem.id)}
                          className="p-2 hover:bg-red-50 rounded transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={18} className="text-red-500" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No se encontraron usuarios
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Users;
